import { useState, useCallback, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Shield, Plus, Trash2, FileText, Settings, RefreshCw } from "lucide-react";
import type { AdminBudgetItem, AdminPulseData } from "@shared/schema";

interface AdminPulseDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function createEmptyBudgetItem(): AdminBudgetItem {
  return {
    id: generateId(),
    label: "",
    type: "bid",
    bidSent: false,
    bidRejected: false,
    bidApproved: false,
    invoiceCreated: false,
    invoiceSent: false,
    invoicePaid: false,
  };
}

export function AdminPulseDialog({ open, onClose, projectId }: AdminPulseDialogProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("admin_notes");
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [budgetLoaded, setBudgetLoaded] = useState(false);
  const budgetLoadedForProject = useRef<string | null>(null);
  const [existingBudgetItemIds, setExistingBudgetItemIds] = useState<Set<string>>(new Set());

  const [adminNotes, setAdminNotes] = useState("");
  const [budgetItems, setBudgetItems] = useState<AdminBudgetItem[]>([createEmptyBudgetItem()]);
  const [systemEventType, setSystemEventType] = useState("");
  const [systemDetails, setSystemDetails] = useState("");

  const resetForm = useCallback(() => {
    setAdminNotes("");
    setBudgetItems([createEmptyBudgetItem()]);
    setSystemEventType("");
    setSystemDetails("");
    setActiveTab("admin_notes");
    setBudgetLoaded(false);
    setExistingBudgetItemIds(new Set());
    budgetLoadedForProject.current = null;
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const loadLatestBudget = useCallback(async () => {
    if (!projectId) return;

    setLoadingBudget(true);
    try {
      const res = await fetch(`/api/budget-items/${encodeURIComponent(projectId)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();

      if (Array.isArray(data) && data.length > 0) {
        const existingItems = data.map((item: any) => ({
          id: item.id,
          label: item.label || '',
          type: item.type || 'bid',
          parentBidId: item.parentBidId,
          bidSent: !!item.bidSent,
          bidRejected: !!item.bidRejected,
          bidApproved: !!item.bidApproved,
          invoiceCreated: !!item.invoiceCreated,
          invoiceSent: !!item.invoiceSent,
          invoicePaid: !!item.invoicePaid,
          notes: item.notes,
        }));
        setBudgetItems(existingItems);
        setExistingBudgetItemIds(new Set(data.map((i: any) => i.id)));
      } else {
        setBudgetItems([createEmptyBudgetItem()]);
        setExistingBudgetItemIds(new Set());
      }
      setBudgetLoaded(true);
      budgetLoadedForProject.current = projectId;
    } catch (err) {
      console.error("[AdminPulseDialog] Failed to load budget items:", err);
      setBudgetItems([createEmptyBudgetItem()]);
      setExistingBudgetItemIds(new Set());
      setBudgetLoaded(true);
      budgetLoadedForProject.current = projectId;
    } finally {
      setLoadingBudget(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && activeTab === "budgeting" && !budgetLoaded && budgetLoadedForProject.current !== projectId) {
      loadLatestBudget();
    }
  }, [open, activeTab, budgetLoaded, projectId, loadLatestBudget]);

  const addBudgetItem = useCallback(() => {
    setBudgetItems(prev => [...prev, createEmptyBudgetItem()]);
  }, []);

  const removeBudgetItem = useCallback((id: string) => {
    setBudgetItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const updateBudgetItem = useCallback((id: string, updates: Partial<AdminBudgetItem>) => {
    setBudgetItems(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
  }, []);

  const handleSubmit = async () => {
    if (activeTab === "budgeting") {
      const validItems = budgetItems.filter(i => i.label.trim());
      if (validItems.length === 0) {
        toast({ title: "Required", description: "Add at least one budget item with a label.", variant: "destructive" });
        return;
      }

      setSubmitting(true);
      try {
        const currentIds = new Set(validItems.map(i => i.id));
        const deletedIds = Array.from(existingBudgetItemIds).filter(id => !currentIds.has(id));

        for (const id of deletedIds) {
          await apiRequest("DELETE", `/api/budget-items/${id}`);
        }

        for (const item of validItems) {
          const payload = {
            id: item.id,
            projectId,
            label: item.label,
            type: item.type,
            parentBidId: item.parentBidId || null,
            bidSent: item.bidSent,
            bidRejected: item.bidRejected,
            bidApproved: item.bidApproved,
            invoiceCreated: item.invoiceCreated,
            invoiceSent: item.invoiceSent,
            invoicePaid: item.invoicePaid,
            notes: item.notes || null,
          };

          if (existingBudgetItemIds.has(item.id)) {
            await apiRequest("PATCH", `/api/budget-items/${item.id}`, payload);
          } else {
            await apiRequest("POST", "/api/budget-items", payload);
          }
        }

        queryClient.invalidateQueries({ queryKey: ["/api/budget-items-bulk"] });
        queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"] });
        queryClient.invalidateQueries({ queryKey: ["/api/projects"] });

        toast({ title: "Budget saved", description: "Budget items have been saved successfully." });
        handleClose();
      } catch (error: any) {
        toast({ title: "Error", description: error?.message || "Failed to save budget items.", variant: "destructive" });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    let adminData: AdminPulseData;

    if (activeTab === "admin_notes") {
      if (!adminNotes.trim()) {
        toast({ title: "Required", description: "Please enter admin notes.", variant: "destructive" });
        return;
      }
      adminData = { subType: "admin_notes", adminNotes: { text: adminNotes.trim() } };
    } else {
      if (!systemEventType.trim()) {
        toast({ title: "Required", description: "Please enter an event type.", variant: "destructive" });
        return;
      }
      adminData = {
        subType: "system_update",
        systemUpdate: { eventType: systemEventType.trim(), details: systemDetails.trim() || undefined },
      };
    }

    setSubmitting(true);
    try {
      const payload: any = {
        projectId,
        subType: adminData.subType,
      };
      if (adminData.subType === 'admin_notes') {
        payload.adminNotes = adminData.adminNotes;
      } else if (adminData.subType === 'system_update') {
        payload.systemUpdate = adminData.systemUpdate;
      }
      const res = await apiRequest("POST", "/api/admin-pulses", payload);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save admin pulse");
      }

      queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin-pulses"] });

      toast({ title: "Admin pulse created", description: "The admin pulse has been added." });
      handleClose();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to create admin pulse.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const bidItems = budgetItems.filter(i => i.type === "bid");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-[680px] max-h-[85vh] overflow-y-auto bg-gray-900 text-gray-100 border-gray-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-100">
            <Shield className="w-5 h-5 text-amber-400" />
            {activeTab === 'budgeting' && existingBudgetItemIds.size > 0 ? 'Edit Budget' : 'Create Admin Pulse'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-3 bg-gray-800 border border-gray-700">
            <TabsTrigger value="admin_notes" className="text-xs data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              <FileText className="w-3 h-3 mr-1" />
              Admin Notes
            </TabsTrigger>
            <TabsTrigger value="budgeting" className="text-xs data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              <Shield className="w-3 h-3 mr-1" />
              Budgeting
            </TabsTrigger>
            <TabsTrigger value="system_update" className="text-xs data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              <Settings className="w-3 h-3 mr-1" />
              System Update
            </TabsTrigger>
          </TabsList>

          <TabsContent value="admin_notes" className="mt-3 space-y-2">
            <Label className="text-xs text-gray-400">Notes</Label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Enter admin notes..."
              className="min-h-[160px] bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 text-sm"
            />
          </TabsContent>

          <TabsContent value="budgeting" className="mt-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-400">Budget Items</Label>
                {budgetLoaded && budgetItems.some(i => i.label.trim()) && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300">
                    Loaded from previous snapshot
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={loadLatestBudget}
                  disabled={loadingBudget}
                  className="h-7 text-xs text-gray-400 hover:text-gray-200"
                  title="Reload latest budget state"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${loadingBudget ? 'animate-spin' : ''}`} />
                  Reload
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={addBudgetItem} className="h-7 text-xs text-blue-400 hover:text-blue-300">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
            </div>

            {loadingBudget ? (
              <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Loading previous budget state...
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {budgetItems.map((item, idx) => (
                  <div key={item.id} className="p-3 bg-gray-800 border border-gray-700 rounded-md space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-4">{idx + 1}</span>
                      <Input
                        value={item.label}
                        onChange={(e) => updateBudgetItem(item.id, { label: e.target.value })}
                        placeholder="Label (e.g. Main Bid, Invoice #1)"
                        className="flex-1 h-8 bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500 text-sm"
                      />
                      <Select value={item.type} onValueChange={(v: "bid" | "invoice") => updateBudgetItem(item.id, { type: v })}>
                        <SelectTrigger className="w-28 h-8 bg-gray-900 border-gray-600 text-gray-100 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          <SelectItem value="bid" className="text-gray-100 text-xs">Bid</SelectItem>
                          <SelectItem value="invoice" className="text-gray-100 text-xs">Invoice</SelectItem>
                        </SelectContent>
                      </Select>
                      {budgetItems.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeBudgetItem(item.id)} className="h-8 w-8 p-0 text-gray-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>

                    {item.type === "invoice" && bidItems.length > 0 && (
                      <div className="flex items-center gap-2 pl-6">
                        <Label className="text-[10px] text-gray-500 whitespace-nowrap">Parent Bid</Label>
                        <Select value={item.parentBidId || "__none__"} onValueChange={(v) => updateBudgetItem(item.id, { parentBidId: v === "__none__" ? undefined : v })}>
                          <SelectTrigger className="h-7 bg-gray-900 border-gray-600 text-gray-100 text-xs flex-1">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600">
                            <SelectItem value="__none__" className="text-gray-400 text-xs">None</SelectItem>
                            {bidItems.map(b => (
                              <SelectItem key={b.id} value={b.id} className="text-gray-100 text-xs">
                                {b.label || `Bid ${budgetItems.indexOf(b) + 1}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-x-4 gap-y-1 pl-6">
                      {([
                        ["bidRejected", "Bid Rejected"],
                        ["bidSent", "Bid Sent"],
                        ["bidApproved", "Bid Approved"],
                        ["invoiceCreated", "Invoice Created"],
                        ["invoiceSent", "Invoice Sent"],
                        ["invoicePaid", "Invoice Paid"],
                      ] as const).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                          <Checkbox
                            checked={!!item[key]}
                            onCheckedChange={(v) => {
                              const updates: Partial<AdminBudgetItem> = { [key]: !!v };
                              if (key === 'bidRejected' && v) updates.bidApproved = false;
                              if (key === 'bidApproved' && v) updates.bidRejected = false;
                              updateBudgetItem(item.id, updates);
                            }}
                            className="h-3.5 w-3.5 border-gray-500"
                          />
                          <span className="text-[11px] text-gray-400">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="system_update" className="mt-3 space-y-3">
            <div className="space-y-2">
              <Label className="text-xs text-gray-400">Event Type</Label>
              <Input
                value={systemEventType}
                onChange={(e) => setSystemEventType(e.target.value)}
                placeholder="e.g. status_change, config_update"
                className="h-8 bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-400">Details</Label>
              <Textarea
                value={systemDetails}
                onChange={(e) => setSystemDetails(e.target.value)}
                placeholder="Optional details about this system event..."
                className="min-h-[120px] bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 text-sm"
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-700">
          <Button type="button" variant="ghost" onClick={handleClose} className="text-gray-400 hover:text-gray-200">
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white">
            {submitting 
              ? (activeTab === 'budgeting' ? "Saving..." : "Creating...") 
              : activeTab === 'budgeting' 
                ? "Save Budget" 
                : "Create Admin Pulse"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
